import { useState, useEffect, useCallback } from "react"
import { useBoolean } from "helpers/hooks"
import {
  FormProjectRequest,
  defaultFormProjectRequest,
  FormProjectProps,
  CategoryDetail,
  ProjectDetail,
} from "../types"
import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { MESSENGER_NOTIFICATION } from "constants/messenger"
import FormInputCard from "pages/auth/molecules/FormInputCard"
import InputDefault from "components/Input/InputDefault"
import MultipleInput from "components/Input/MultipleInput"
import ModalCustom from "components/ModalCustom"
import SelectMulti from "components/Select/SelectMulti"
import {
  addRoleGroupOrganizationMiddleware,
  getCategoriesMiddleware,
  handleUserShareKeyProjectMiddleware,
  postCategoryMiddleware,
  postProjectMiddleware,
  putProjectMiddleware,
} from "../services/api"
import { STATUS_RESPONSE } from "types"
import { cloneDeep, debounce } from "lodash"
import ActionFooterModalCard from "components/ModalCustom/ActionFooterModalCard"
import { umamiTracking } from "helpers/utils"
import { EVENT } from "constants/events"
import { useAppSelector } from "hooks/useApp"
import {
  getUserAddGroupsOrganizationMiddleware,
  postAssignProjectToGroupsOrganizationMiddleware,
} from "pages/groups/groups.api"
import { configureStore } from "stores/configureStore"
import { closeLoading, startLoading } from "reducers/loading"
import { encryptionController } from "controllers/EncryptionController"
import { ActivityLogType, activityLogHelper } from "helpers/activity_log"
import { postActionLogMiddleware } from "components/activity-log/activity-log.api"

const FormProject = (props: FormProjectProps) => {
  const {
    defaultRequest,
    openModal,
    handleChangeData,
    onCloseModal,
    groups,
    isDisableGroup,
  } = props
  const userInfo = useAppSelector((state) => state.userInfo)
  const isLoading = useBoolean()
  const [request, setRequest] = useState<FormProjectRequest>(
    defaultFormProjectRequest
  )
  const [selectedCategory, setSelectedCategory] = useState<CategoryDetail[]>([])
  const [listCategories, setListCategories] = useState<CategoryDetail[]>([])
  const isLoadingSearch = useBoolean(false)
  const [selectedOrganization, setSelectedOrganization] = useState<
    {
      label: string
      value: string
    }[]
  >([])
  const [selectedUsers, setSelectedUsers] = useState<
    {
      label: string
      value: string
    }[]
  >([])

  const [users, setUsers] = useState<
    {
      id: string
      fullname: string
      email: string
    }[]
  >([])

  useEffect(() => {
    if (!openModal) {
      return
    }
    setRequest({
      ...request,
      name: defaultRequest.name,
      description: defaultRequest.description,
    })
    setSelectedCategory(defaultRequest.selectCategories ?? [])
    getCategories()
    if (userInfo.last_organization_id) {
      getUsers()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultRequest, openModal, userInfo.last_organization_id])

  useEffect(() => {
    if (!isDisableGroup || !openModal || !groups.length) {
      return
    }
    setSelectedOrganization([
      {
        label: groups[0].code + " - " + groups[0].name,
        value: groups[0].id,
      },
    ])
  }, [isDisableGroup, openModal, JSON.stringify(groups)])

  const getUsers = async () => {
    const userResponse = await getUserAddGroupsOrganizationMiddleware("all")
    setUsers(userResponse)
  }

  const getCategories = async (newInputName = "", loading = true) => {
    isLoading.setValue(loading)
    const params = {
      page: 1,
      pageSize: 7,
      filter: {
        name: newInputName,
      },
    }

    try {
      const dataRes = await getCategoriesMiddleware(
        `/api/category/get-user-categories`,
        params
      )
      const newDataRes: CategoryDetail[] = dataRes.map((el) => {
        return {
          ...el,
          label: el.name,
          value: el.id,
        }
      })
      setListCategories(newDataRes)
      isLoadingSearch.setValue(false)
      isLoading.setValue(false)
    } catch (error) {
      setListCategories([])
      isLoading.setValue(false)
    }
  }

  const handleChangeInput = (key: "name" | "description") => (event) => {
    if (key === "description" && event.target.value.length > 150) {
      return
    }
    setRequest({
      ...request,
      [key]: event.target.value,
    })
  }

  const handleDisableButton = () => {
    if (request.description.length > 150) {
      return true
    }
    if (!request.name) {
      return true
    }
    return false
  }
  const onSubmit = () => {
    if (request.description.length > 150) {
      toast(
        <LabelNotificationPage
          messenger={MESSENGER_NOTIFICATION.DESCRIPTION_ERROR}
          type="error"
        />
      )
      return
    }
    if (selectedCategory.length === 0) {
      toast(
        <LabelNotificationPage
          messenger={MESSENGER_NOTIFICATION.REQUICE_CREATE_CATEGORY_ERROR}
          type="error"
        />
      )
      return
    }
    const dataRequest: any = {
      name: request.name,
      description: request.description,
      category_ids: selectedCategory.map((el) => el.id),
    }
    isLoading.setValue(true)
    if (defaultRequest.id) {
      putProjectMiddleware(
        defaultRequest.id,
        dataRequest,
        (type: STATUS_RESPONSE, messenger: string, dataRes?: ProjectDetail) => {
          toast(<LabelNotificationPage messenger={messenger} type={type} />)
          isLoading.setValue(false)
          if (type === STATUS_RESPONSE.SUCCESS && dataRes) {
            onCloseModal()
            handleChangeData(dataRes)
          }
        },
        defaultRequest
      )
      return
    }
    configureStore.dispatch(startLoading())

    postProjectMiddleware(
      dataRequest,
      async (
        type: STATUS_RESPONSE,
        messenger: string,
        projectKey: string,
        projectId: string
      ) => {
        if (type === STATUS_RESPONSE.SUCCESS) {
          if (projectKey && projectId && userInfo.last_organization_id) {
            await handleUserShareKeyProjectMiddleware(
              true,
              true,
              JSON.stringify([projectId])
            )
            await addRoleGroupOrganizationMiddleware({
              is_share_all_project: true,
              project_ids: JSON.stringify([projectId]),
            })

            if (selectedOrganization.length) {
              for (const el of selectedOrganization) {
                const content = await encryptionController().encrypt(
                  activityLogHelper.toLogMessage(
                    ActivityLogType.AssignProjectToGroup,
                    {
                      groupName: el.label,
                    }
                  ),
                  {
                    dataType: "string",
                    type: "project",
                    encryptionKey: projectKey,
                  }
                )
                try {
                  await postAssignProjectToGroupsOrganizationMiddleware({
                    project_id: projectId,
                    group_organization_id: el.value,
                  })
                  await postActionLogMiddleware(
                    {
                      content: content,
                      created_by: localStorage.getItem("user_id") || "",
                      model: "projects",
                      relation_id: projectId,
                    },
                    true
                  )
                  await handleUserShareKeyProjectMiddleware(
                    false,
                    true,
                    JSON.stringify([projectId]),
                    el.value
                  )
                } catch (error) {}
              }
            }
            if (selectedUsers.length) {
              for (const elUser of selectedUsers) {
                try {
                  await postAssignProjectToGroupsOrganizationMiddleware({
                    project_id: projectId,
                    user_id: elUser.value,
                  })
                  const content = await encryptionController().encrypt(
                    activityLogHelper.toLogMessage(
                      ActivityLogType.AssignProjectToUser,
                      {
                        userName: elUser.label,
                      }
                    ),
                    {
                      dataType: "string",
                      type: "project",
                      encryptionKey: projectKey,
                    }
                  )
                  await postActionLogMiddleware(
                    {
                      content: content,
                      created_by: localStorage.getItem("user_id") || "",
                      model: "projects",
                      relation_id: projectId,
                    },
                    true
                  )
                } catch (error) {}
              }
              await handleUserShareKeyProjectMiddleware(
                false,
                true,
                JSON.stringify([projectId]),
                undefined,
                JSON.stringify(selectedUsers.map((el) => el.value))
              )
            }
            const dataRequest: {
              is_share_all_project: boolean
              project_ids?: string
              group_ids?: string
              user_ids?: string
            } = {
              is_share_all_project: false,
              project_ids: JSON.stringify([projectId]),
            }
            if (selectedOrganization.length) {
              dataRequest.group_ids = JSON.stringify(
                selectedOrganization.map((el) => el.value)
              )
            }
            if (selectedUsers.length) {
              dataRequest.user_ids = JSON.stringify(
                selectedUsers.map((el) => el.value)
              )
            }
            if (dataRequest.project_ids || dataRequest.user_ids) {
              await addRoleGroupOrganizationMiddleware(dataRequest)
            }
          }
          toast(<LabelNotificationPage messenger={messenger} type={type} />)
          isLoading.setValue(false)
          closeLoading()
          onCloseModal()
          handleChangeData()

          return
        }
        toast(<LabelNotificationPage messenger={messenger} type={type} />)
        isLoading.setValue(false)
        closeLoading()
      }
    )
    umamiTracking(EVENT.PROJECT.CREATE)
  }

  const handleChange = (newValue) => {
    setSelectedCategory(newValue)
  }
  const handleCreateSelect = (newSelect) => async () => {
    if (!newSelect) {
      return
    }
    postCategoryMiddleware(
      {
        name: newSelect,
      },
      (type: STATUS_RESPONSE, messenger: string, data?: CategoryDetail) => {
        if (type === STATUS_RESPONSE.ERROR) {
          toast(<LabelNotificationPage messenger={messenger} type={type} />)
          return
        }
        if (data) {
          const newlistCategories = cloneDeep(listCategories)
          newlistCategories.push({
            id: data.id,
            name: data.name,
            value: data.id,
            label: data.name,
          })
          setListCategories(newlistCategories)
        }
      }
    )
  }
  const handleInputChange = (newValueInput: string) => {
    isLoadingSearch.setValue(true)
    debounceSearch(newValueInput)
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearch = useCallback(
    debounce((newSearch: string) => {
      getCategories(newSearch, false)
    }, 200),
    []
  )

  return (
    <ModalCustom
      label={`${defaultRequest.id ? "Edit project" : "Create new Project"}`}
      handleChangeButton={() => onCloseModal}
      bodyChildren={
        <div className="flex flex-col">
          <FormInputCard
            title="Project Name"
            required
            fontWeightText="font-semibold"
          >
            <InputDefault
              value={request.name}
              onChange={handleChangeInput("name")}
            />
          </FormInputCard>
          <FormInputCard title="Description" fontWeightText="font-semibold">
            <MultipleInput
              value={request.description}
              onChange={handleChangeInput("description")}
              rows={4}
              maxLength={150}
            />
          </FormInputCard>
          <FormInputCard
            title="Category"
            required
            fontWeightText="font-semibold"
          >
            <SelectMulti
              options={listCategories}
              selectedOption={selectedCategory}
              handleChange={handleChange}
              handleCreateSelect={handleCreateSelect}
              handleInputChange={handleInputChange}
              isLoadingSearch={isLoadingSearch.value}
            />
          </FormInputCard>
          {!defaultRequest.id &&
          groups.length &&
          userInfo.last_organization_id ? (
            <FormInputCard
              title="Assign to groups"
              fontWeightText="font-semibold"
            >
              <SelectMulti
                options={groups.map((el) => ({
                  label: `${el.code} - ${el.name}`,
                  value: el.id,
                }))}
                selectedOption={selectedOrganization}
                handleChange={(dataChoses) => {
                  setSelectedOrganization(dataChoses)
                }}
                isMulti={isDisableGroup ? false : true}
                noOptionsMessage
                isDisabled={isDisableGroup}
              />
            </FormInputCard>
          ) : null}
          {!defaultRequest.id &&
          !isDisableGroup &&
          users.length &&
          userInfo.last_organization_id ? (
            <FormInputCard
              title="Assign to users"
              fontWeightText="font-semibold"
            >
              <SelectMulti
                options={users.map((el) => ({
                  label: `${el.fullname} - ${el.email}`,
                  value: el.id,
                }))}
                selectedOption={selectedUsers}
                handleChange={(dataChoses) => {
                  setSelectedUsers(dataChoses)
                }}
                noOptionsMessage
              />
            </FormInputCard>
          ) : null}
          <ActionFooterModalCard
            onCloseModal={onCloseModal}
            onSubmit={onSubmit}
            handleDisableButton={handleDisableButton()}
            titleButton={defaultRequest.id ? "Save" : "Create"}
          />
        </div>
      }
    />
  )
}

export default FormProject
